import type { SearchBackendWire } from '../api.js'
import type { JSX } from 'react'

/**
 * Which search backend an instance has, in words (IM-182).
 *
 * The derivation is `deployment.ts`'s, not this file's: the console renders
 * the answer rather than deciding it, so a list and the API cannot disagree
 * about which backend is running.
 *
 * Words people already use for the two installs — the AWS managed service or
 * ECK inside the cluster — because "which kind of Elasticsearch" was the
 * question that asked for this column, and the variable names answer it only
 * for someone who has read the installer.
 */
const LABELS: Readonly<Record<string, string>> = {
  opensearch: 'OpenSearch (AWS)',
  elasticsearch: 'Elasticsearch (in cluster)',
  'installer-default': 'Installer default',
}

/**
 * The backend, its size underneath, and "(requested)" when it is not what is
 * running.
 *
 * - **Absent renders a dash**, never a blank cell: an API older than this
 *   field, or a succeeded apply recorded without variables. A blank reads as a
 *   rendering fault.
 * - **A backend this build does not know renders raw**, the rule every status
 *   here follows, so a new one cannot pass unnoticed for a release.
 * - **`requested` is said in words.** Nothing has been applied, so this is a
 *   plan or a failed apply's ask; unmarked it would read as a fact about an
 *   instance that does not exist yet.
 */
export const SearchBackendLabel = ({
  search,
}: {
  readonly search: SearchBackendWire | undefined
}): JSX.Element => {
  if (search === undefined) return <>—</>
  return (
    <>
      {LABELS[search.backend] ?? search.backend}
      {search.basis === 'requested' ? <span className="muted"> (requested)</span> : null}
      {search.size === undefined ? null : <p className="muted">{search.size}</p>}
    </>
  )
}
